import JSZip from 'jszip';
import { SkillActivation } from '@shared/models/skills/SkillModel';

/**
 * In-browser build/parse of a skill package zip, used by the admin portal (which has no local
 * filesystem) to create and read org skills through the REST API. A skill package is a zip whose root
 * `SKILL.md` holds YAML frontmatter (name/description/keywords/activation) followed by the markdown
 * instructions body, per the Agent Skills spec. The desktop app produces/consumes the same shape.
 *
 * Only the metadata-and-body subset is handled here; supporting files (scripts/references/assets) are
 * a native-desktop-only affordance and are never part of a portal-authored package.
 */

const FRONTMATTER_DELIMITER = '---';
const BOM = '\uFEFF';

interface SkillFrontmatter {
    name: string;
    description: string;
    keywords?: string[];
    activation?: SkillActivation;
}

/** Serialize a minimal YAML frontmatter block (we control both sides, so no YAML lib is needed). */
function buildSkillMarkdown(front: SkillFrontmatter, body: string): string {
    const lines: string[] = [
        FRONTMATTER_DELIMITER,
        `name: ${front.name}`,
        `description: ${yamlScalar(front.description)}`,
    ];
    if (front.keywords && front.keywords.length > 0) {
        lines.push(`keywords: [${front.keywords.map((k) => yamlScalar(k)).join(', ')}]`);
    }
    if (front.activation) {
        lines.push(`activation: ${front.activation}`);
    }
    lines.push(FRONTMATTER_DELIMITER, '', body ?? '');
    return lines.join('\n');
}

/** Quote a scalar if it contains characters that would break simple `key: value` YAML. */
function yamlScalar(value: string): string {
    const v = value ?? '';
    return /[:#\n"']/.test(v) ? JSON.stringify(v) : v;
}

/** Strip a leading YAML frontmatter block from SKILL.md and return the markdown body. */
export function stripFrontmatter(markdown: string): string {
    const text = markdown.startsWith(BOM) ? markdown.slice(BOM.length) : markdown;
    if (!text.startsWith(FRONTMATTER_DELIMITER)) return text.trim();
    const end = text.indexOf(`\n${FRONTMATTER_DELIMITER}`, FRONTMATTER_DELIMITER.length);
    if (end === -1) return text.trim();
    const afterDelimiter = text.indexOf('\n', end + 1);
    return afterDelimiter === -1 ? '' : text.slice(afterDelimiter + 1).trim();
}

/** Build a skill package zip (containing `SKILL.md`) as a File ready for `uploadSkillPackage`. */
export async function buildSkillPackage(front: SkillFrontmatter, body: string | undefined): Promise<File> {
    const zip = new JSZip();
    zip.file('SKILL.md', buildSkillMarkdown(front, body ?? ''));
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    return new File([blob], 'skill.zip', { type: 'application/zip' });
}

/** Read the markdown instructions body from a downloaded skill package zip. */
export async function parseSkillPackageBody(data: ArrayBuffer | Blob): Promise<string> {
    const zip = await JSZip.loadAsync(data);
    // The spec puts SKILL.md at the root; tolerate a single top-level folder wrapper too.
    const entry = zip.file('SKILL.md') ?? zip.file(/(^|\/)SKILL\.md$/i)[0] ?? null;
    if (!entry) return '';
    const markdown = await entry.async('string');
    return stripFrontmatter(markdown);
}
